/**
 * WorldMapLive — lightweight WebSocket listener for world/marker-revealed
 * broadcasts. Follows the same shape as {@link LoreLive}:
 *
 *   - Own socket, own scope, not tied to SceneClient (which is map-scoped
 *     and would drop broadcasts when no map is mounted).
 *   - Backoff + reconnect is identical to LoreLive; we never send anything
 *     on this socket so there's no resubscribe step.
 *   - Dispatch filters on "world/" prefix so scene/* and lore/* traffic is
 *     ignored even if a single tab ends up with multiple listeners.
 *
 * Typical use on MapViewer / MapViewerIndex / Dashboard:
 *
 *   const live = new WorldMapLive();
 *   const off = live.on("world/marker-revealed", (p) => {
 *     push("info", `A new location has been revealed: ${p.label}`, {
 *       action: { onClick: () => navigate(`/map/${p.mapSlug ?? p.mapId}`) },
 *     });
 *     if (p.mapId === currentMapId) void refreshMarkers();
 *   });
 *   void live.start();
 *   return () => { off(); live.stop(); };
 *
 * Why a dedicated client rather than bolting onto LoreLive? The two features
 * ship independently and the payload filtering is per-namespace. Colocating
 * them would tangle their dispatch tables and force every consumer to import
 * payloads they don't care about.
 */

import { openSocket } from './socket';
import {
  WorldMapServerTypes,
  type ServerEnvelope,
  type WorldMapMarkerRevealedPayload,
} from '../vtt/net/sceneWire';

/**
 * Map from world-map event type string to its payload shape. Extend this
 * when new world/* server events land on the wire.
 */
export interface WorldMapEventMap {
  [WorldMapServerTypes.MarkerRevealed]: WorldMapMarkerRevealedPayload;
}

type Listener<T> = (payload: T) => void;

export class WorldMapLive {
  private ws: WebSocket | null = null;
  private stopped = false;
  private backoffMs = 500;
  private readonly maxBackoffMs: number;
  /** Keyed on event-type string, value is the set of callbacks. */
  private readonly listeners = new Map<string, Set<Listener<unknown>>>();

  constructor(opts: { maxBackoffMs?: number } = {}) {
    this.maxBackoffMs = opts.maxBackoffMs ?? 15000;
  }

  /**
   * Register a callback for a world-map event type. Returns an unsubscribe
   * function. Multiple subscribers can register on the same type; they're
   * called in registration order.
   */
  on<K extends keyof WorldMapEventMap>(
    type: K,
    cb: Listener<WorldMapEventMap[K]>,
  ): () => void {
    const key = type as string;
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    const wrapper = cb as Listener<unknown>;
    set.add(wrapper);
    return () => {
      set!.delete(wrapper);
      if (set!.size === 0) this.listeners.delete(key);
    };
  }

  /** True while the connect loop is running (not stopped). */
  private running = false;

  async start(): Promise<void> {
    // Idempotent: if another caller has already started the singleton we
    // share the existing connection rather than opening a second socket.
    if (this.running) return;
    this.running = true;
    this.stopped = false;
    try {
      await this.connectLoop();
    } finally {
      this.running = false;
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private async connectLoop(): Promise<void> {
    while (!this.stopped) {
      try {
        const ws = await openSocket();
        this.ws = ws;
        await this.waitForOpen(ws);
        this.backoffMs = 500;
        ws.addEventListener('message', (ev) => this.onMessage(ev.data));
        await this.waitForClose(ws);
      } catch (err) {
        console.warn('[WorldMapLive] connect failed', err);
      }
      this.ws = null;
      if (this.stopped) break;
      const jitter = Math.random() * 200;
      await new Promise((r) => setTimeout(r, this.backoffMs + jitter));
      this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
    }
  }

  private waitForOpen(ws: WebSocket): Promise<void> {
    if (ws.readyState === WebSocket.OPEN) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onOpen = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        resolve();
      };
      const onError = (ev: Event) => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        reject(new Error(`WebSocket error before open: ${ev.type}`));
      };
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });
  }

  private waitForClose(ws: WebSocket): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        ws.removeEventListener('close', done);
        ws.removeEventListener('error', done);
        resolve();
      };
      ws.addEventListener('close', done);
      ws.addEventListener('error', done);
    });
  }

  /** True when the live connection is currently attempting to run. */
  isRunning(): boolean {
    return this.running;
  }

  private onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let env: ServerEnvelope;
    try {
      env = JSON.parse(raw);
    } catch {
      return;
    }
    // Only dispatch world/* events — scene/* and lore/* are owned by
    // SceneClient / LoreLive and ignored here.
    if (!env.type || !env.type.startsWith('world/')) return;
    const set = this.listeners.get(env.type);
    if (!set || set.size === 0) return;
    for (const cb of set) {
      try {
        cb(env.payload);
      } catch (err) {
        console.warn('[WorldMapLive] listener threw', err);
      }
    }
  }
}

/**
 * Module-level singleton. The AppShell mounts this once so a `world/*`
 * broadcast fires a toast regardless of which page the user is on; the
 * MapViewer subscribes to the same instance to refresh its marker list
 * when an event arrives for the map it's currently rendering. One socket
 * per tab is cheaper than one per subscribing page.
 *
 * The returned instance is lazily constructed and never replaced — calling
 * stop() leaves the singleton instance in place so a subsequent start()
 * reuses the same listener table.
 */
let _singleton: WorldMapLive | null = null;
export function getWorldMapLive(): WorldMapLive {
  if (!_singleton) _singleton = new WorldMapLive();
  return _singleton;
}
